import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import { getBaseApiUrl } from "./EnvironmentManager";
import { getClientTimeZone } from "../utilities/DateUtil";

export enum OrderStatusEnum {
  Draft = "draft",
  Pending = "pending",
  Submitted = "submitted",
  Processing = "processing",
  Shipped = "shipped",
  Delivered = "delivered",
  Canceled = "canceled",
  Refunded = "refunded",
  Failed = "error",
}
export type OrderStatus = `${OrderStatusEnum}`; // optional

export interface TrackingInfo {
  trackingNumber?: string;
  trackingUrl?: string;
  carrier?: string;
  shippedDate?: string;
  estimatedDelivery?: string;
  status?: "pending" | "shipped" | "in_transit" | "delivered" | "exception";
}

export interface Order {
  orderId?: string;
  customerOrderId?: string;
  userId?: string;
  createdDate?: Date;
  updatedDate?: Date;
  externalId?: string;
  lineItems?: LineItem[];
  shippingDetails?: ShippingDetails;
  amountSubtotal?: number;
  amountTax?: number;
  amountDiscount?: number;
  amountShipping?: number;
  amountTotal?: number;
  currency?: "USD";
  payment?: Payment;
  timeZone?: string;
  status?: OrderStatus;
  tracking?: TrackingInfo[];
}

export interface LineItem {
  productId?: string;
  productUserId?: string;
  name?: string;
  description?: string;
  catalogItemId: string;
  catalogItemVariantId: string;
  vendorData?: any;
  quantity: number;
  price?: number;
  metadata?: any;
  fulfillmentStatus?: "fulfilled" | "unfulfilled";
  tracking?: TrackingInfo;
}

export interface Address {
  country: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postalCode: string;
}

export interface ShippingDetails {
  name: string;
  email?: string;
  phone?: string;
  address: Address;
  method?: string;
}

export interface Payment {
  vendorName?: string;          // 'stripe'
  paymentSessionId?: string;    // cs_*
  paymentIntentId?: string;     // pi_*
  status?: string;              // 'processing' | 'succeeded' | 'paid' | ...
  paymentType?: string;         // 'card'
  amountTotal?: number;         // major units, e.g. 12.34
  currency?: string;            // 'USD'
  paidAt?: string;              // ISO
  receiptUrl?: string;
  buyerEmail?: string;
  cardBrand?: string;           // 'visa'
  cardLast4?: string;           // '4242'
  metadata?: any;
};

const getOrderApiUrl = () => {
  return `${getBaseApiUrl()}/order`;
};

const useOrderApi = () => {
  const { user, getAccessTokenSilently } = useAuth0();

  const createOrder = async (cartItems: LineItem[], payment: Payment): Promise<Order> => {
    let order: Order = {
      userId: user?.sub || "0",
      lineItems: cartItems,
      payment,
      status: "pending",
    };
    return order;
  };

  const submitOrder = async (order: Order): Promise<Order> => {
    const endpointUrl = getOrderApiUrl();
    order.status = "submitted";
    order.timeZone = getClientTimeZone();
    try {
      const headers: any = {
        "Content-Type": "application/json"
      };

      // Debug logging for user state
      console.log('Order submission - User object:', user);
      console.log('Order submission - User sub:', user?.sub);
      console.log('Order submission - User authenticated:', !!user?.sub);

      // Include auth token if user is logged in
      if (user?.sub) {
        try {
          console.log('Attempting to get access token...');
          const accessToken = await getAccessTokenSilently({
            detailedResponse: true
          });

          if (accessToken?.access_token) {
            headers["Authorization"] = `Bearer ${accessToken.access_token}`;
            console.log('Successfully added auth token to headers');
            console.log('Token preview:', accessToken.access_token.substring(0, 20) + '...');
          } else if (typeof accessToken === 'string') {
            headers["Authorization"] = `Bearer ${accessToken}`;
            console.log('Successfully added auth token to headers (string format)');
            console.log('Token preview:', (accessToken as string).substring(0, 20) + '...');
          } else {
            console.warn('Access token format unexpected:', typeof accessToken);
          }

          console.log('Final headers:', Object.keys(headers));
        } catch (authError) {
          console.error('Failed to get access token:', authError);
          console.warn('Submitting as anonymous due to auth error');
        }
      } else {
        console.log('No user.sub available, submitting as anonymous user');
      }

      const response = await axios.post(endpointUrl, order, { headers });
      if (
        (response?.status === 200 || response?.status === 201) &&
        response?.data?.data
      ) {
        return response.data.data as Order;
      } else {
        throw new Error(
          "No Order found in the response or status is not success."
        );
      }
    } catch (error: any) {
      console.error("Error submitting an order:", error);
      throw new Error("Failed to create Order");
    }
  };

  const updateOrder = async (order: Order): Promise<Order> => {
    const endpointUrl = getOrderApiUrl();
    try {
      const response = await axios.put(endpointUrl, order, {
        headers: {
          "Content-Type": "application/json"
        },
      });

      if (response?.status === 200 && response?.data?.data) {
        return response.data.data as Order;
      } else {
        throw new Error("No Order found in response");
      }
    } catch (error: any) {
      console.log("Error updating the order:", error?.message);
      throw new Error("Failed to update the order");
    }
  };

  const getOrder = async (orderId: string, userId: string): Promise<Order> => {
    const params = new URLSearchParams({
      userId: userId,
    });

    const endpointUrl = `${getOrderApiUrl()}/${orderId}?${params.toString()}`;
    try {
      const response = await axios.get(endpointUrl, {
        headers: {
          "Content-Type": "application/json"
        },
      });
      if (response?.status === 200 && response?.data?.data) {
        return response.data.data as Order;
      } else {
        throw new Error(
          "No order data found in the response or status is not success."
        );
      }
    } catch (error: any) {
      console.error("Error calling API:", error?.message);
      throw new Error("Failed to retrieve order data from the API");
    }
  };

  const getUserOrders = async (userId: string = "0", limit: number = 10, lastKey?: Record<string, any>): Promise<{ items: Array<Object>, lastKey: Record<string, any> | null }> => {
    const params = new URLSearchParams({
      userId: userId,
      limit: limit.toString(),
      lastKey: lastKey
        ? JSON.stringify(lastKey) // Encode the lastKey as a JSON string
        : "",
    });

    const endpointUrl = `${getOrderApiUrl()}?${params.toString()}`;
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await axios.get(endpointUrl, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        }
      });

      if (response?.status === 200 && response?.data?.status === 'success' && response?.data?.data) {
        return response.data.data;
      } else {
        throw new Error('No order data found in the response or status is not success.');
      }
    } catch (error) {
      console.error('Error calling API:', JSON.stringify(error));
      throw new Error('Failed to retrieve order data from the API');
    }
  };

  const updateOrderTracking = async (orderId: string): Promise<Order> => {
    const token = await getAccessTokenSilently();
    const endpointUrl = `${getOrderApiUrl()}/${orderId}?action=updateTracking`;

    try {
      const response = await axios.get(endpointUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response?.status === 200 && response?.data?.data) {
        return response.data.data as Order;
      } else {
        throw new Error('No order data found in the response or status is not success.');
      }
    } catch (error: any) {
      console.error('Error updating order tracking:', error);
      throw new Error('Failed to update order tracking');
    }
  };

  return { createOrder, submitOrder, getOrder, updateOrder, getUserOrders, updateOrderTracking };
};

export default useOrderApi;
